import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  FormControl,
  InputLabel,
  OutlinedInput,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
} from '@mui/material';
import { Close } from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  Legend,
  Cell,
} from 'recharts';
import { SpendingByCategoryItem, Transaction } from '../services/plaidService';
import { cachedSpendingByCategory } from '../services/prefetch';

// Mercury muted earth-tone palette
const PALETTE = [
  '#0f7a5a', '#5b4d3a', '#4a5b8a', '#b8842a', '#c43e3e',
  '#7a6a8a', '#0a6478', '#8a6a3a', '#6c7a89', '#9a7a5a',
  '#3d8b6b', '#a06840', '#2d6e8e', '#7a5a3a', '#5a8a6a',
];

const PRETTY: { [key: string]: string } = {
  FOOD_AND_DRINK: 'Food & Drink',
  TRANSPORTATION: 'Transportation',
  GENERAL_MERCHANDISE: 'Shopping',
  ENTERTAINMENT: 'Entertainment',
  RENT_AND_UTILITIES: 'Rent & Utilities',
  LOAN_PAYMENTS: 'Loan Payments',
  GENERAL_SERVICES: 'Services',
  PERSONAL_CARE: 'Personal Care',
  TRAVEL: 'Travel',
  TRANSFER_OUT: 'Transfers Out',
  TRANSFER_IN: 'Transfers In',
  GOVERNMENT_AND_NON_PROFIT: 'Government & Non-profit',
  HOME_IMPROVEMENT: 'Home Improvement',
  HEALTHCARE: 'Healthcare',
  INSURANCE: 'Insurance',
  EDUCATION: 'Education',
  INCOME: 'Income',
  BANK_FEES: 'Bank Fees',
  OTHER: 'Other',
};

const prettify = (key: string): string =>
  PRETTY[key] || key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const formatCurrency = (n: number): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);

const formatCurrencyShort = (v: number): string =>
  v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${v.toFixed(0)}`;

const SHORT_MONTH = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Essential spending — excluded from the discretionary view.
// Primary categories hidden entirely (rent, utilities, mortgages, loans):
const ESSENTIAL_PRIMARY = new Set<string>([
  'LOAN_PAYMENTS',
  'RENT_AND_UTILITIES',
]);
// Detailed sub-categories hidden within their primary bucket (groceries):
const ESSENTIAL_DETAILED = new Set<string>([
  'FOOD_AND_DRINK_GROCERIES',
]);

interface MonthBucket {
  key: string;       // e.g. "2026-05"
  label: string;     // e.g. "May 2026"
  start: string;     // "YYYY-MM-DD"
  end: string;       // "YYYY-MM-DD"
  byCategory: { [category: string]: number };
  txByCategory: { [category: string]: Transaction[] };
  total: number;
}

// Past 3 *full* months — excludes the current (partial) month.
// e.g. on 2026-05-24, returns Feb 2026, Mar 2026, Apr 2026.
const buildLast3Months = (now: Date): MonthBucket[] => {
  const buckets: MonthBucket[] = [];
  for (let i = 3; i >= 1; i--) {
    const start = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const end = new Date(now.getFullYear(), now.getMonth() - i + 1, 0);
    const y = start.getFullYear();
    const m = start.getMonth();
    buckets.push({
      key: `${y}-${String(m + 1).padStart(2, '0')}`,
      label: `${SHORT_MONTH[m]} ${y}`,
      start: start.toISOString().split('T')[0],
      end: end.toISOString().split('T')[0],
      byCategory: {},
      txByCategory: {},
      total: 0,
    });
  }
  return buckets;
};

const SpendingByCategoryChart: React.FC = () => {
  const [buckets, setBuckets] = useState<MonthBucket[]>([]);
  const [allCategories, setAllCategories] = useState<string[]>([]);
  const [selected, setSelected] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogCategory, setDialogCategory] = useState<string>('');
  const [dialogMonthLabel, setDialogMonthLabel] = useState<string>('');
  const [dialogTransactions, setDialogTransactions] = useState<Transaction[]>([]);
  const [dialogColor, setDialogColor] = useState<string>('#0f7a5a');
  const [hoveredCategory, setHoveredCategory] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        const monthBuckets = buildLast3Months(new Date());
        const results = await Promise.all(
          monthBuckets.map((b) => cachedSpendingByCategory(b.start, b.end)),
        );
        if (cancelled) return;

        const categoryTotals: { [key: string]: number } = {};
        results.forEach((res, idx) => {
          const cats: SpendingByCategoryItem[] = res.categories || [];
          let total = 0;
          cats.forEach((c) => {
            // Skip primary categories considered essential (rent, utilities, loans)
            if (ESSENTIAL_PRIMARY.has(c.category)) return;

            // Filter out essential detailed sub-types (e.g. groceries within FOOD_AND_DRINK)
            const filteredTx = (c.transactions || []).filter((t) => {
              const detailed = t.personal_finance_category?.detailed;
              return !(detailed && ESSENTIAL_DETAILED.has(detailed));
            });
            const catTotal = filteredTx.length === (c.transactions || []).length
              ? c.total
              : filteredTx.reduce((sum, t) => sum + Math.abs(t.amount), 0);

            if (catTotal <= 0) return; // entire category was essential

            monthBuckets[idx].byCategory[c.category] = catTotal;
            monthBuckets[idx].txByCategory[c.category] = filteredTx;
            total += catTotal;
            categoryTotals[c.category] = (categoryTotals[c.category] || 0) + catTotal;
          });
          monthBuckets[idx].total = total;
        });

        // Sort categories by total spending (largest first) so colors are stable + dropdown is intuitive
        const sortedCats = Object.keys(categoryTotals).sort(
          (a, b) => categoryTotals[b] - categoryTotals[a],
        );

        setBuckets(monthBuckets);
        setAllCategories(sortedCats);
        setSelected(sortedCats); // default: all selected
      } catch (e) {
        if (!cancelled) setError('Failed to load spending by category');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  const colorByCategory = useMemo(() => {
    const map: { [key: string]: string } = {};
    allCategories.forEach((c, i) => { map[c] = PALETTE[i % PALETTE.length]; });
    return map;
  }, [allCategories]);

  // Build chart data: one row per month, one numeric field per selected category
  const chartData = useMemo(() => {
    return buckets.map((b) => {
      const row: { [key: string]: any } = { month: b.label };
      let visibleTotal = 0;
      selected.forEach((cat) => {
        const v = Math.round((b.byCategory[cat] || 0) * 100) / 100;
        row[cat] = v;
        visibleTotal += v;
      });
      row.__total = Math.round(visibleTotal * 100) / 100;
      return row;
    });
  }, [buckets, selected]);

  const windowTotal = useMemo(
    () => chartData.reduce((s, r) => s + (r.__total || 0), 0),
    [chartData],
  );

  const handleSelectChange = (event: any) => {
    const value = event.target.value;
    setSelected(typeof value === 'string' ? value.split(',') : value);
  };

  const selectAll = () => setSelected(allCategories);
  const selectNone = () => setSelected([]);

  const handleBarClick = (category: string, monthLabel: string) => {
    const bucket = buckets.find((b) => b.label === monthLabel);
    if (!bucket) return;
    const txs = bucket.txByCategory[category] || [];
    if (txs.length === 0) return;
    setDialogCategory(category);
    setDialogMonthLabel(monthLabel);
    setDialogTransactions([...txs].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
    ));
    setDialogColor(colorByCategory[category] || '#0f7a5a');
    setDialogOpen(true);
  };

  const handleCloseDialog = () => setDialogOpen(false);

  const formatDate = (dateString: string) =>
    new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

  const dialogTotal = dialogTransactions.reduce((s, t) => s + Math.abs(t.amount), 0);

  // Custom tooltip — show each segment's amount + the month total
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;
    const sorted = [...payload].sort((a: any, b: any) => (b.value || 0) - (a.value || 0));
    const total = sorted.reduce((s: number, p: any) => s + (p.value || 0), 0);
    return (
      <Box
        sx={{
          backgroundColor: '#ffffff',
          border: '1px solid #eaeaea',
          borderRadius: 2,
          px: 1.5,
          py: 1,
          boxShadow: '0 4px 12px rgba(15,15,15,0.06)',
          minWidth: 220,
          maxWidth: 280,
        }}
      >
        <Typography variant="caption" sx={{ color: '#8a8a8a', fontWeight: 500, display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
        <Typography sx={{ fontSize: '0.95rem', fontWeight: 600, color: '#0a0a0a', fontVariantNumeric: 'tabular-nums', mb: 0.75 }}>
          {formatCurrency(total)}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.375 }}>
          {sorted.filter((p: any) => p.value > 0).slice(0, 8).map((p: any) => (
            <Box key={p.dataKey} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: p.color, flexShrink: 0 }} />
              <Typography variant="caption" sx={{ color: '#5c5c5c', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {prettify(p.dataKey)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#0a0a0a', fontWeight: 500, fontVariantNumeric: 'tabular-nums', flexShrink: 0 }}>
                {formatCurrency(p.value)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  const periodLabel = buckets.length
    ? `${buckets[0].label} – ${buckets[buckets.length - 1].label}`
    : 'Last 3 months';

  return (
    <>
    <Card sx={{ mb: 3, border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
      <CardContent sx={{ p: { xs: 3, sm: 4 } }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 2, mb: 2.5 }}>
          <Box>
            <Typography variant="body2" sx={{ color: '#8a8a8a', fontWeight: 500, mb: 0.5 }}>
              Discretionary spending by category · {periodLabel}
            </Typography>
            <Typography variant="caption" sx={{ color: '#a0a0a0', display: 'block', mb: 0.5 }}>
              Excludes rent, utilities, loan payments, and groceries
            </Typography>
            <Typography sx={{ fontWeight: 600, fontSize: '1.375rem', letterSpacing: '-0.015em', color: '#0a0a0a', fontVariantNumeric: 'tabular-nums' }}>
              {formatCurrency(windowTotal)}
            </Typography>
          </Box>

          {/* Category dropdown */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 260 }}>
              <InputLabel id="cat-select-label">Categories</InputLabel>
              <Select
                labelId="cat-select-label"
                multiple
                value={selected}
                onChange={handleSelectChange}
                input={<OutlinedInput label="Categories" />}
                renderValue={(picked) => {
                  if (picked.length === 0) return <em style={{ color: '#8a8a8a' }}>None selected</em>;
                  if (picked.length === allCategories.length) return `All categories (${picked.length})`;
                  if (picked.length <= 2) {
                    return (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {picked.map((c) => (
                          <Chip
                            key={c}
                            label={prettify(c)}
                            size="small"
                            sx={{ height: 20, fontSize: '0.7rem', bgcolor: colorByCategory[c], color: '#fff' }}
                          />
                        ))}
                      </Box>
                    );
                  }
                  return `${picked.length} of ${allCategories.length} selected`;
                }}
                MenuProps={{
                  PaperProps: { sx: { maxHeight: 360 } },
                }}
                sx={{ fontSize: '0.85rem' }}
              >
                <Box sx={{ display: 'flex', gap: 0.5, px: 1, py: 0.5, borderBottom: '1px solid #f0f0f0' }}>
                  <Button
                    size="small"
                    onClick={selectAll}
                    sx={{ fontSize: '0.7rem', textTransform: 'none', color: '#5c5c5c', minWidth: 0, px: 1 }}
                  >
                    All
                  </Button>
                  <Button
                    size="small"
                    onClick={selectNone}
                    sx={{ fontSize: '0.7rem', textTransform: 'none', color: '#5c5c5c', minWidth: 0, px: 1 }}
                  >
                    None
                  </Button>
                </Box>
                {allCategories.map((cat) => (
                  <MenuItem key={cat} value={cat} sx={{ py: 0.5 }}>
                    <Checkbox checked={selected.indexOf(cat) > -1} size="small" sx={{ p: 0.5, mr: 0.5 }} />
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: colorByCategory[cat], mr: 1, flexShrink: 0 }} />
                    <ListItemText
                      primary={prettify(cat)}
                      primaryTypographyProps={{ fontSize: '0.85rem' }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>

        {/* Body */}
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
            <CircularProgress size={24} />
          </Box>
        ) : buckets.length === 0 || allCategories.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <Typography variant="body2" sx={{ color: '#8a8a8a' }}>
              No spending data for the last 3 months
            </Typography>
          </Box>
        ) : selected.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 320 }}>
            <Typography variant="body2" sx={{ color: '#8a8a8a' }}>
              Select at least one category from the dropdown
            </Typography>
          </Box>
        ) : (
          <Box sx={{ height: 380 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" vertical={false} />
                <XAxis
                  dataKey="month"
                  tick={{ fontSize: 12, fill: '#5c5c5c' }}
                  tickLine={false}
                  axisLine={{ stroke: '#eaeaea' }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#5c5c5c' }}
                  tickLine={false}
                  axisLine={{ stroke: '#eaeaea' }}
                  tickFormatter={formatCurrencyShort}
                />
                <RechartsTooltip content={<CustomTooltip />} cursor={{ fill: 'rgba(15,122,90,0.06)' }} />
                <Legend
                  wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
                  iconType="circle"
                  iconSize={8}
                  onMouseEnter={(o: any) => o && o.dataKey && setHoveredCategory(o.dataKey as string)}
                  onMouseLeave={() => setHoveredCategory(null)}
                  formatter={(value: string) => {
                    const isHovered = hoveredCategory === value;
                    return (
                      <span
                        style={{
                          color: isHovered ? '#0a0a0a' : '#5c5c5c',
                          fontWeight: isHovered ? 600 : 400,
                          cursor: 'pointer',
                        }}
                      >
                        {prettify(value)}
                      </span>
                    );
                  }}
                />
                {selected.map((cat, idx) => {
                  const isHovered = hoveredCategory === cat;
                  return (
                    <Bar
                      key={cat}
                      dataKey={cat}
                      stackId="spending"
                      fill={colorByCategory[cat]}
                      // Only round the top of the topmost segment
                      radius={idx === selected.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                      style={{ cursor: 'pointer' }}
                      onClick={(data: any) => {
                        if (data && data.month) handleBarClick(cat, data.month);
                      }}
                      onMouseEnter={() => setHoveredCategory(cat)}
                      onMouseLeave={() => setHoveredCategory(null)}
                      isAnimationActive={false}
                    >
                      {chartData.map((row, i) => (
                        <Cell
                          key={`${cat}-${i}`}
                          fill={colorByCategory[cat]}
                          stroke={isHovered ? '#ffffff' : 'transparent'}
                          strokeWidth={isHovered ? 2 : 0}
                          style={{
                            filter: isHovered ? 'brightness(1.08) drop-shadow(0 2px 6px rgba(0,0,0,0.18))' : 'none',
                            transition: 'filter 120ms ease',
                          }}
                        />
                      ))}
                    </Bar>
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          </Box>
        )}
      </CardContent>
    </Card>

    {/* Transactions dialog */}
    <Dialog
      open={dialogOpen}
      onClose={handleCloseDialog}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}
    >
      <DialogTitle sx={{ pb: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: dialogColor }} />
            <Box>
              <Typography sx={{ fontSize: '1.05rem', fontWeight: 600, color: '#0a0a0a' }}>
                {prettify(dialogCategory)}
              </Typography>
              <Typography variant="caption" sx={{ color: '#8a8a8a' }}>
                {dialogMonthLabel} · {dialogTransactions.length} transaction{dialogTransactions.length === 1 ? '' : 's'} · {formatCurrency(dialogTotal)}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleCloseDialog} size="small">
            <Close fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        {dialogTransactions.length === 0 ? (
          <Box textAlign="center" py={4}>
            <Typography variant="body2" sx={{ color: '#8a8a8a' }}>
              No transactions to show
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ boxShadow: 'none', borderColor: '#eaeaea' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: '#fafafa' }}>
                  <TableCell sx={{ fontSize: '0.75rem', color: '#8a8a8a', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4 }}>Date</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: '#8a8a8a', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4 }}>Merchant</TableCell>
                  <TableCell sx={{ fontSize: '0.75rem', color: '#8a8a8a', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4 }}>Description</TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.75rem', color: '#8a8a8a', fontWeight: 500, textTransform: 'uppercase', letterSpacing: 0.4 }}>Amount</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dialogTransactions.map((t) => (
                  <TableRow key={t.transaction_id} hover>
                    <TableCell sx={{ fontSize: '0.85rem', color: '#5c5c5c', whiteSpace: 'nowrap' }}>
                      {formatDate(t.date)}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.85rem', color: '#0a0a0a' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {t.logo_url ? (
                          <Avatar src={t.logo_url} sx={{ width: 22, height: 22, bgcolor: '#f5f5f5' }} />
                        ) : (
                          <Avatar sx={{ width: 22, height: 22, bgcolor: '#f0f0f0', fontSize: '0.7rem', color: '#8a8a8a' }}>
                            {(t.merchant_name || t.name || '?').charAt(0).toUpperCase()}
                          </Avatar>
                        )}
                        <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                          {t.merchant_name || t.name}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', color: '#5c5c5c', maxWidth: 300 }}>
                      <Typography variant="caption" noWrap sx={{ display: 'block' }}>
                        {t.name}
                      </Typography>
                    </TableCell>
                    <TableCell align="right" sx={{ fontSize: '0.9rem', fontWeight: 600, color: '#0a0a0a', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
                      {formatCurrency(Math.abs(t.amount))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleCloseDialog} sx={{ textTransform: 'none' }}>Close</Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default React.memo(SpendingByCategoryChart);
